import Img from '../components/Img';
﻿import React, { useEffect, useState } from 'react';
import { Helmet } from '@dr.pogodin/react-helmet';
import { Link } from 'react-router-dom';
import MailingListForm from '../MailingListForm';

const SMS_NUMBER = '5082031342';
const DISPLAY_NUMBER = '508-203-1342';

const BEFORE_AFTER_PREVIEWS = [
  { src: '/assets/before-after/Tattoo Removal Ink-01.png', alt: 'Before and after tattoo removal' },
  { src: '/assets/before-after/Tattoo Removal Ink-02.png', alt: 'Before and after tattoo removal' },
  { src: '/assets/before-after/Joshua 600.png', alt: 'Before and after tattoo removal' },
  { src: '/assets/before-after/Star 600c.png', alt: 'Before and after tattoo removal' },
];

const WHY_ATOMIC = [
  { icon: '🏠', title: 'Locally Owned & Operated', desc: 'Not a franchise or corporate chain. You work directly with owner Craig Howard.' },
  { icon: '💬', title: 'Personal Communication', desc: 'All texts are answered directly by Craig — not a robot 🤖, call center, or franchise office.' },
  { icon: '🎯', title: 'Astanza Trinity Laser', desc: 'State-of-the-art 3-wavelength laser targets all colors including stubborn blues and greens.' },
  { icon: '✅', title: 'Free Consultation', desc: 'Get honest pricing, realistic expectations, and a personalized plan — no pressure, no obligation.' },
];

// ── Phone Modal ────────────────────────────────────────────────
const PhoneModal: React.FC<{ onClose: () => void }> = ({ onClose }) => (
  <div
    onClick={onClose}
    style={{
      position: 'fixed', inset: 0, zIndex: 2000,
      background: 'rgba(0,0,0,0.45)',
      display: 'flex', alignItems: 'center', justifyContent: 'center',
    }}
  >
    <div
      onClick={e => e.stopPropagation()}
      style={{
        background: '#fff', borderRadius: 16, padding: '32px 40px',
        textAlign: 'center', boxShadow: '0 8px 40px rgba(0,0,0,0.22)',
        maxWidth: 340, width: '90%',
      }}
    >
      <p style={{ fontSize: '0.78rem', fontWeight: 700, letterSpacing: '0.1em', textTransform: 'uppercase', color: '#5C96AC', marginBottom: 8 }}>
        Text Us
      </p>
      <p style={{ fontSize: 'clamp(1.6rem, 6vw, 2.2rem)', fontWeight: 800, color: '#1e3a5f', marginBottom: 8 }}>
        {DISPLAY_NUMBER}
      </p>
      <p style={{ color: '#555', fontSize: '0.9rem', lineHeight: 1.6, marginBottom: 20 }}>
        Text us your availability for a free consultation and we'll help you find the best time for your appointment.
      </p>
      <button
        onClick={onClose}
        style={{
          background: '#cb4f2e', color: '#fff', border: 'none', borderRadius: 50,
          padding: '10px 28px', fontWeight: 700, fontSize: '0.95rem', cursor: 'pointer',
        }}
      >
        Close
      </button>
    </div>
  </div>
);

// ── SmsButton ────────────────────────────────────────────────────
interface SmsButtonProps {
  isMobile: boolean;
  onShowModal: () => void;
  trackLocation: string;
  trackClick: (loc: string) => void;
  style?: React.CSSProperties;
  children: React.ReactNode;
}
const SmsButton: React.FC<SmsButtonProps> = ({ isMobile, onShowModal, trackLocation, trackClick, style, children }) => {
  if (isMobile) {
    return (
      <a
        href={`sms:${SMS_NUMBER}`}
        onClick={() => trackClick(trackLocation)}
        style={style}
      >
        {children}
      </a>
    );
  }
  return (
    <button
      onClick={() => { trackClick(trackLocation); onShowModal(); }}
      style={{ ...style, cursor: 'pointer', border: 'none' }}
    >
      {children}
    </button>
  );
};

// ── Landing ───────────────────────────────────────────────────────
const Landing: React.FC = () => {
  const [showStickyBar, setShowStickyBar] = useState(false);
  const [showPhoneModal, setShowPhoneModal] = useState(false);
  const [isMobile, setIsMobile] = useState(false);

  useEffect(() => {
    const mq = window.matchMedia('(hover: none) and (pointer: coarse)');
    setIsMobile(mq.matches);
    const handler = (e: MediaQueryListEvent) => setIsMobile(e.matches);
    mq.addEventListener('change', handler);
    return () => mq.removeEventListener('change', handler);
  }, []);

  useEffect(() => {
    const handleScroll = () => setShowStickyBar(window.scrollY > 400);
    window.addEventListener('scroll', handleScroll, { passive: true });
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const trackClick = (location: string) => {
    (window as any).dataLayer = (window as any).dataLayer || [];
    (window as any).dataLayer.push({ event: 'book_now_click', button_location: location });
  };

  return (
    <>
      {showPhoneModal && <PhoneModal onClose={() => setShowPhoneModal(false)} />}
      <Helmet>
        <title>Atomic Tattoo Removal | Professional Laser Tattoo Removal in Wrentham, MA</title>
        <meta name="description" content="Atomic Tattoo Removal — locally owned laser tattoo removal in Wrentham, MA. Free consultation. Text a photo of your tattoo for honest pricing and realistic expectations." />
        <meta name="keywords" content="tattoo removal, laser tattoo removal, free consultation, Wrentham MA, Franklin MA, Foxboro MA, Norfolk MA, Plainville MA, Massachusetts tattoo removal" />
        <meta property="og:title" content="Atomic Tattoo Removal | Free Consultation — Wrentham, MA" />
        <meta property="og:description" content="Locally owned laser tattoo removal in Wrentham, MA. Text us a photo for honest pricing and realistic expectations." />
        <meta property="og:image" content="https://www.atomictattooremoval.com/assets/staff.png" />
      </Helmet>

      {/* ── HERO ── */}
      <div style={{ textAlign: 'center', padding: '32px 16px 24px', maxWidth: 700, margin: '0 auto' }}>
        <h1 style={{ fontSize: 'clamp(1.6rem, 5vw, 2.4rem)', fontWeight: 800, color: '#1e3a5f', marginBottom: 8, lineHeight: 1.2 }}>
          Professional Laser Tattoo Removal
        </h1>
        <p style={{ color: '#5C96AC', fontSize: 'clamp(0.95rem, 2.5vw, 1.1rem)', fontWeight: 600, marginBottom: 24 }}>
          Wrentham, MA · Serving MetroWest &amp; Greater Boston
        </p>

        <Img
          src="/assets/staff.png"
          alt="The Atomic Tattoo Removal team — three friendly laser removal technicians in Wrentham, MA"
          style={{
            // Sized by width so the browser reserves space before load (no
            // layout shift / CLS); height follows the aspect ratio. maxWidth
            // 610 ≈ 320px tall on desktop for this image (height = width / 1.9).
            width: '100%',
            maxWidth: 610,
            height: 'auto',
            borderRadius: 16,
            boxShadow: '0 6px 24px rgba(30,58,95,0.14)',
            display: 'block',
            margin: '0 auto 12px',
          }}
        />
        <p style={{ color: '#777', fontSize: '0.88rem', fontStyle: 'italic', marginBottom: 20 }}>
          Meet our team — safe, effective, and supportive laser tattoo removal.
        </p>

        <div style={{ display: 'flex', flexWrap: 'wrap', gap: 10, justifyContent: 'center', marginBottom: 8 }}>
          <span style={{
            display: 'inline-block', background: '#f0f7fa', border: '1px solid #c2dae6',
            borderRadius: 8, padding: '8px 14px', color: '#2d5986', fontSize: '0.85rem', fontWeight: 600,
          }}>
            ✅ Individually owned — not a franchise
          </span>
          <span style={{
            display: 'inline-block', background: '#f0f7fa', border: '1px solid #c2dae6',
            borderRadius: 8, padding: '8px 14px', color: '#2d5986', fontSize: '0.85rem', fontWeight: 600,
          }}>
            💬 Texts answered by owner Craig Howard — not a robot 🤖
          </span>
        </div>
      </div>

      {/* ── FREE CONSULTATION CTA ── */}
      <div style={{
        background: 'linear-gradient(135deg, #1e3a5f 0%, #2d5986 100%)',
        padding: 'clamp(28px, 5vw, 44px) clamp(16px, 5vw, 40px)',
        textAlign: 'center',
      }}>
        <p style={{ fontSize: '0.78rem', fontWeight: 700, letterSpacing: '0.12em', textTransform: 'uppercase', color: '#90c8e0', marginBottom: 8 }}>
          No Pressure · No Obligation
        </p>
        <h2 style={{ fontSize: 'clamp(1.5rem, 4vw, 2.2rem)', fontWeight: 800, color: '#fff', marginBottom: 12, lineHeight: 1.2 }}>
          Free Tattoo Removal Consultation
        </h2>
        <p style={{ color: '#d0e8f5', fontSize: 'clamp(0.95rem, 2.5vw, 1.05rem)', maxWidth: 480, margin: '0 auto 24px', lineHeight: 1.7 }}>
          Get <strong style={{ color: '#fff' }}>honest expectations</strong>, <strong style={{ color: '#fff' }}>pricing</strong>, and a{' '}
          <strong style={{ color: '#fff' }}>personalized treatment plan</strong>.<br />
          Text us a photo of your tattoo or book your free consultation today.
        </p>

        <div style={{ display: 'flex', flexWrap: 'wrap', gap: 14, justifyContent: 'center', marginBottom: 16 }}>
          <SmsButton
            isMobile={isMobile}
            onShowModal={() => setShowPhoneModal(true)}
            trackLocation="hero_text"
            trackClick={trackClick}
            style={{
              display: 'inline-block', background: '#cb4f2e', color: '#fff', fontWeight: 800,
              fontSize: 'clamp(1rem, 3vw, 1.15rem)', padding: '16px 36px', borderRadius: 50,
              textDecoration: 'none', letterSpacing: '0.02em', boxShadow: '0 4px 18px rgba(203,79,46,0.45)',
            }}
          >
            📱 Text Us Now
          </SmsButton>
          <Link
            to="/book-consultation"
            onClick={() => trackClick('hero_book')}
            style={{
              display: 'inline-block', background: 'transparent', color: '#fff', fontWeight: 800,
              fontSize: 'clamp(1rem, 3vw, 1.15rem)', padding: '16px 36px', borderRadius: 50,
              textDecoration: 'none', border: '2px solid rgba(255,255,255,0.65)',
            }}
          >
            📅 Book Free Consultation
          </Link>
        </div>
        <p style={{ color: '#90c8e0', fontSize: '0.88rem', margin: 0 }}>{DISPLAY_NUMBER}</p>
      </div>

      {/* ── LOCAL TRUST BAR ── */}
      <div style={{ background: '#f0f7fa', borderBottom: '1px solid #c2dae6', padding: '12px 16px', textAlign: 'center' }}>
        <p style={{ color: '#2d5986', fontSize: '0.88rem', fontWeight: 600, margin: 0 }}>
          📍 Serving Wrentham, Franklin, Foxboro, Norfolk, Plainville, and surrounding Massachusetts areas
        </p>
      </div>

      {/* ── BEFORE / AFTER RESULTS ── */}
      <div style={{ maxWidth: 760, margin: '0 auto', padding: '36px 16px 28px', textAlign: 'center' }}>
        <h2 style={{ fontSize: 'clamp(1.3rem, 4vw, 1.8rem)', fontWeight: 800, color: '#1e3a5f', marginBottom: 8 }}>
          Real Results with the Astanza Trinity Laser
        </h2>
        <p style={{ color: '#555', fontSize: '0.97rem', marginBottom: 24 }}>
          See what is possible with multiple sessions. Results vary based on ink, skin type, and tattoo age.
        </p>
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(160px, 1fr))', gap: 12, marginBottom: 20 }}>
          {BEFORE_AFTER_PREVIEWS.map(({ src, alt }) => (
            <Img
              key={src}
              src={src}
              alt={alt}
              style={{ width: '100%', borderRadius: 10, boxShadow: '0 2px 12px rgba(30,58,95,0.10)', display: 'block' }}
            />
          ))}
        </div>
        <Link to="/before-after-images" style={{ color: '#cb4f2e', fontWeight: 700, fontSize: '0.97rem', textDecoration: 'underline' }}>
          View full before &amp; after gallery →
        </Link>
      </div>

      {/* ── WHY ATOMIC ── */}
      <div style={{ background: '#fff', padding: '32px 16px 36px', textAlign: 'center' }}>
        <div style={{ maxWidth: 700, margin: '0 auto' }}>
          <h2 style={{ fontSize: 'clamp(1.3rem, 4vw, 1.8rem)', fontWeight: 800, color: '#1e3a5f', marginBottom: 24 }}>
            Why Choose Atomic Tattoo Removal?
          </h2>
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', gap: 20, textAlign: 'left' }}>
            {WHY_ATOMIC.map(({ icon, title, desc }) => (
              <div key={title} style={{ background: '#f0f7fa', borderRadius: 12, padding: '20px 18px' }}>
                <div style={{ fontSize: '1.8rem', marginBottom: 8 }}>{icon}</div>
                <h3 style={{ fontWeight: 700, color: '#1e3a5f', fontSize: '1rem', marginBottom: 6 }}>{title}</h3>
                <p style={{ color: '#555', fontSize: '0.9rem', lineHeight: 1.6, margin: 0 }}>{desc}</p>
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* ── MID-PAGE CTA ── */}
      <div style={{ textAlign: 'center', padding: '36px 16px' }}>
        <h2 style={{ fontSize: 'clamp(1.2rem, 3.5vw, 1.6rem)', fontWeight: 800, color: '#1e3a5f', marginBottom: 10 }}>
          Ready to Get Started?
        </h2>
        <p style={{ color: '#555', fontSize: '0.97rem', maxWidth: 480, margin: '0 auto 22px' }}>
          Text us a photo of your tattoo and we will give you an honest estimate — no pressure, no obligation.
        </p>
        <div style={{ display: 'flex', flexWrap: 'wrap', gap: 14, justifyContent: 'center' }}>
          <SmsButton
            isMobile={isMobile}
            onShowModal={() => setShowPhoneModal(true)}
            trackLocation="mid_page_text"
            trackClick={trackClick}
            style={{
              display: 'inline-block', background: '#cb4f2e', color: '#fff', fontWeight: 800,
              fontSize: '1.05rem', padding: '14px 32px', borderRadius: 50, textDecoration: 'none',
              boxShadow: '0 4px 14px rgba(203,79,46,0.35)',
            }}
          >
            📱 Text Us Now
          </SmsButton>
          <Link
            to="/book-consultation"
            onClick={() => trackClick('mid_page_book')}
            style={{
              display: 'inline-block', background: '#1e3a5f', color: '#fff', fontWeight: 800,
              fontSize: '1.05rem', padding: '14px 32px', borderRadius: 50, textDecoration: 'none',
            }}
          >
            📅 Book Free Consultation
          </Link>
        </div>
      </div>

      {/* ── TECHNOLOGY (educational, lower on page) ── */}
      <div style={{ background: '#f0f7fa', padding: '32px 16px' }}>
        <div style={{ maxWidth: 650, margin: '0 auto', textAlign: 'center' }}>
          <h2 style={{ fontSize: 'clamp(1.2rem, 3.5vw, 1.6rem)', fontWeight: 800, color: '#1e3a5f', marginBottom: 12 }}>
            Advanced Laser Technology
          </h2>
          <p style={{ color: '#555', fontSize: '0.97rem', lineHeight: 1.7, marginBottom: 12 }}>
            We use the{' '}
            <a href="https://astanzalaser.com/technology/trinity/" target="_blank" rel="noopener noreferrer" style={{ color: '#cb4f2e', textDecoration: 'underline' }}>
              Astanza Trinity laser system
            </a>
            {' '}— a state-of-the-art system combining three wavelengths (1064 nm, 532 nm, and 694 nm) to target a wide range of tattoo colors, including stubborn blues and greens. Treatment is precisely tailored to your unique tattoo.
          </p>
          <Link to="/our-technology" style={{ color: '#cb4f2e', fontWeight: 700, textDecoration: 'underline' }}>
            Learn more about our technology →
          </Link>
        </div>
      </div>

      {/* ── MAILING LIST ── */}
      <div style={{ backgroundColor: '#5C96AC', padding: '20px' }}>
        <MailingListForm />
      </div>

      {/* ── STICKY MOBILE CTA BAR ── */}
      <div style={{
        position: 'fixed',
        bottom: 0,
        left: 0,
        right: 0,
        zIndex: 1000,
        background: '#1e3a5f',
        borderTop: '2px solid #5C96AC',
        padding: '10px 12px',
        display: 'flex',
        gap: 10,
        justifyContent: 'center',
        transform: showStickyBar ? 'translateY(0)' : 'translateY(100%)',
        transition: 'transform 0.3s ease',
      }}>
        <SmsButton
          isMobile={isMobile}
          onShowModal={() => setShowPhoneModal(true)}
          trackLocation="sticky_text"
          trackClick={trackClick}
          style={{
            flex: 1, maxWidth: 180, display: 'inline-block', background: '#cb4f2e',
            color: '#fff', fontWeight: 700, fontSize: '0.92rem', padding: '12px 0',
            borderRadius: 50, textDecoration: 'none', textAlign: 'center',
          }}
        >
          📱 Text Us
        </SmsButton>
        <Link
          to="/book-consultation"
          onClick={() => trackClick('sticky_book')}
          style={{
            flex: 1, maxWidth: 200, display: 'inline-block', background: 'transparent',
            color: '#fff', fontWeight: 700, fontSize: '0.92rem', padding: '12px 0',
            borderRadius: 50, textDecoration: 'none', textAlign: 'center',
            border: '2px solid rgba(255,255,255,0.5)',
          }}
        >
          📅 Book Consultation
        </Link>
      </div>

      {showStickyBar && <div style={{ height: 64 }} />}
    </>
  );
};

export default Landing;
